import { useEffect, useRef, useState } from "react";
import { X, Send, Sparkles, Loader2 } from "lucide-react";
import { http } from "@/lib/api";

const SUGGESTIONS = [
    "Which regions grew the most last 30 days?",
    "Where are we most at risk of missing month-end targets?",
    "Show me the top stores by sell-out this month.",
    "Which SKUs are at dead-stock risk?",
    "Summarize open critical alerts.",
];

export default function CopilotPanel({ open, onClose }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sessionId, setSessionId] = useState(null);
    const [loading, setLoading] = useState(false);
    const endRef = useRef(null);

    useEffect(() => {
        if (open) setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    }, [open, messages, loading]);

    async function send(q) {
        const question = (q ?? input).trim();
        if (!question || loading) return;
        setInput("");
        setMessages((m) => [...m, { role: "user", content: question }]);
        setLoading(true);
        try {
            const { data } = await http.post("/copilot/ask", { question, session_id: sessionId });
            setSessionId(data.session_id);
            setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
        } catch (e) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content: "Copilot is unavailable right now. " + (e.response?.data?.detail || e.message),
                },
            ]);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <div
                className={
                    "fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px] transition-opacity duration-200 " +
                    (open ? "opacity-100" : "pointer-events-none opacity-0")
                }
                onClick={onClose}
            />
            <aside
                data-testid="copilot-panel"
                className={
                    "fixed right-0 top-0 z-50 flex h-screen w-[440px] flex-col border-l border-border bg-card shadow-2xl transition-transform duration-300 " +
                    (open ? "translate-x-0" : "translate-x-full")
                }
            >
                <header className="flex h-14 items-center gap-2 border-b border-border px-4">
                    <div className="grid h-6 w-6 place-items-center rounded-sm bg-[hsl(var(--ai))]/15 text-[hsl(var(--ai))]">
                        <Sparkles className="h-3.5 w-3.5" />
                    </div>
                    <div className="leading-tight">
                        <div className="text-sm font-semibold">Fundle Copilot</div>
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                            Claude Sonnet 4.5 · Governed data
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="copilot-close-btn"
                        className="ml-auto rounded-sm p-1.5 text-muted-foreground hover:bg-muted"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </header>

                <div className="flex-1 space-y-3 overflow-y-auto p-4">
                    {messages.length === 0 && (
                        <div className="space-y-4">
                            <div className="rounded-sm border-l-2 border-[hsl(var(--ai))] bg-[hsl(var(--ai))]/[0.06] p-3 text-[13px] leading-relaxed">
                                Ask questions about sell-out, sell-in, inventory, targets, schemes, and alerts.
                                Copilot only reasons over your tenant's governed metrics — with citations.
                            </div>
                            <div>
                                <div className="mlabel mb-2">Suggested</div>
                                <div className="flex flex-col gap-1.5">
                                    {SUGGESTIONS.map((s, i) => (
                                        <button
                                            key={i}
                                            onClick={() => send(s)}
                                            data-testid={`copilot-suggestion-${i}`}
                                            className="rounded-sm border border-border bg-background px-3 py-2 text-left text-[13px] hover-lift"
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {messages.map((m, i) => (
                        <Bubble key={i} role={m.role} content={m.content} />
                    ))}

                    {loading && (
                        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            Reasoning over governed data…
                        </div>
                    )}
                    <div ref={endRef} />
                </div>

                <form
                    className="flex items-end gap-2 border-t border-border p-3"
                    onSubmit={(e) => {
                        e.preventDefault();
                        send();
                    }}
                >
                    <textarea
                        data-testid="copilot-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                send();
                            }
                        }}
                        placeholder="Ask about sell-out, inventory, targets…"
                        rows={2}
                        className="flex-1 resize-none rounded-sm border border-border bg-background px-3 py-2 text-[13px] outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
                    />
                    <button
                        type="submit"
                        disabled={loading || !input.trim()}
                        data-testid="copilot-send-btn"
                        className="grid h-9 w-9 place-items-center rounded-sm bg-[hsl(var(--ai))] text-white transition-colors disabled:opacity-40"
                    >
                        <Send className="h-4 w-4" />
                    </button>
                </form>
            </aside>
        </>
    );
}

function Bubble({ role, content }) {
    if (role === "user") {
        return (
            <div className="flex justify-end">
                <div className="max-w-[85%] rounded-sm bg-primary/10 px-3 py-2 text-[13px]">
                    {content}
                </div>
            </div>
        );
    }
    return (
        <div className="animate-fade-in-up rounded-sm border-l-2 border-[hsl(var(--ai))] bg-card p-3">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-[hsl(var(--ai))]">
                Copilot
            </div>
            <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed">{content}</pre>
        </div>
    );
}
